import type { Page } from '../../crm/client/pager'
import type { LibraryDeps } from '../LibraryDeps'
import { ALL_MEDIA, allMediaOf, toCategory, toItem, type Category, type MediaType, type VaultItem } from '../vault/vaultData'
import { cachedPage, categoriesKey, mediaKey } from './pickerCache'

/**
 * The picker's OnlyFans requests, shared by the picker and its prefetch so a
 * hover warms exactly the pages the picker then opens on.
 */

/** Bigger than the Vault page's 24: the picker shows ~35 tiles at once. */
export const MEDIA_PAGE = 40
/** 50 categories took ~7 s live (2026-10-07); the Vault page uses 20 too. */
export const CATEGORY_PAGE = 20

export type PickerSort = 'newest' | 'oldest' | 'liked'
/** OnlyFans' own orders (verified live 2026-10-07). */
export const SORT_QUERY: Record<PickerSort, { field: string; sort: string }> = {
  newest: { field: 'recent', sort: 'desc' },
  oldest: { field: 'recent', sort: 'asc' },
  liked: { field: 'most-liked', sort: 'desc' },
}

type ListPayload = { list?: Record<string, unknown>[]; hasMore?: boolean; nextOffset?: number; all?: Record<string, unknown> }
type Gateway = LibraryDeps['gateway']

/** A page of categories; the first one (no search) is cached and carries All media. */
export async function categoriesPage(gateway: Gateway, accountId: string, offset: number, query = ''): Promise<Page<Category> & { all?: Category }> {
  const ask = () => gateway<ListPayload>('/v1/vault/lists', { view: 'main', offset, limit: CATEGORY_PAGE, ...(query ? { query } : {}) })
  const first = offset === 0 && !query
  const p = first ? await cachedPage(categoriesKey(accountId), ask) : await ask()
  return { list: (p.list ?? []).map(toCategory), hasMore: p.hasMore, nextOffset: p.nextOffset, ...(first ? { all: allMediaOf(p) } : {}) }
}

/** A page of media in a category; the first one is cached. */
export async function mediaPage(gateway: Gateway, accountId: string, listId: string, offset: number, type: MediaType | null, sort: PickerSort): Promise<Page<VaultItem>> {
  const ask = () => gateway<ListPayload>('/v1/vault/media', { list_id: listId, offset, limit: MEDIA_PAGE, ...SORT_QUERY[sort], ...(type ? { type } : {}) })
  const p = offset === 0 ? await cachedPage(mediaKey(accountId, listId, type ?? '', sort), ask) : await ask()
  return { list: (p.list ?? []).map(toItem), hasMore: p.hasMore, nextOffset: p.nextOffset }
}

/** Thumbnails a prefetch warms: about the first two rows. */
const PREFETCH_THUMBS = 14

/**
 * Warm what the picker opens on (categories, All media's first page and its
 * first thumbnails) — e.g. while the pointer rests on the Vault button.
 * Failures are left for the picker to show.
 */
export function prefetchPicker(deps: Pick<LibraryDeps, 'gateway' | 'loadMedia'>, accountId: string) {
  void categoriesPage(deps.gateway, accountId, 0).catch(() => undefined)
  void mediaPage(deps.gateway, accountId, ALL_MEDIA, 0, null, 'newest')
    .then((p) =>
      p.list
        .filter((m) => m.type !== 'audio')
        .slice(0, PREFETCH_THUMBS)
        .forEach((m) => void deps.loadMedia(m.small || m.thumb).catch(() => undefined)),
    )
    .catch(() => undefined)
}
